import React from 'react';
import { Duplicate, DuplicateSmall } from '@wix/wix-ui-icons-common';
import { CopyClipboard } from '../../CopyClipboard/CopyClipboard';
import { Tooltip } from '../../Tooltip/Tooltip';
import { TooltipProps } from '../../Tooltip/Tooltip.types';
import { st, classes } from './CopyButton.st.css';
import {
  COPY_BUTTON_SIZE,
  COPY_RESET_TIMEOUT_MS,
  DEFAULT_TOOLTIP_COPIED,
  DEFAULT_TOOLTIP_IDLE,
} from '../CodeSnippet.constants';
import { CodeSnippetVariant, CopyButtonSize } from '../CodeSnippet.types';

type CopyButtonProps = {
  variant: Exclude<CodeSnippetVariant, 'inline'>;
  size?: CopyButtonSize;
  value: string;
  copyTooltipText?: string;
  copiedTooltipText?: string;
  tooltipProps?: Omit<Partial<TooltipProps>, 'content'>;
  onCopy?: (copiedValue: string) => void;
  describedById?: string;
  dataHook?: string;
};

const CopyButton = ({
  variant,
  size = COPY_BUTTON_SIZE.medium,
  value,
  copyTooltipText = DEFAULT_TOOLTIP_IDLE,
  copiedTooltipText = DEFAULT_TOOLTIP_COPIED,
  tooltipProps,
  onCopy,
  describedById,
  dataHook,
}: CopyButtonProps) => (
  <CopyClipboard
    value={value}
    onCopy={() => onCopy?.(value)}
    resetTimeout={COPY_RESET_TIMEOUT_MS}
  >
    {({ isCopied, copyToClipboard }) => {
      const tooltipLabel = isCopied ? copiedTooltipText : copyTooltipText;
      const Icon = size === COPY_BUTTON_SIZE.small ? DuplicateSmall : Duplicate;

      return (
        <Tooltip
          appendTo="window"
          {...tooltipProps}
          content={tooltipLabel}
          aria-describedby={describedById}
        >
          <button
            type="button"
            className={st(classes.root, { variant, size })}
            onClick={copyToClipboard}
            aria-label={tooltipLabel}
            data-hook={dataHook}
          >
            <Icon />
          </button>
        </Tooltip>
      );
    }}
  </CopyClipboard>
);

CopyButton.displayName = 'CodeSnippet.CopyButton';

export { CopyButton };
